import { useEffect, useRef, useState } from 'react';
import { WorkspaceDialog } from '../renderer/WorkspaceDialog';
import '../renderer/confirm-action.css';
import { DestinationIcon } from './DestinationRow';
import { waitingLine } from './remove-question';
import type { PaigeDestination } from './paige.global';
import './remove-destination.css';

export interface RemoveDestinationDialogProps {
  row: PaigeDestination;
  /** The job's other destinations the waiting documents can go to instead (handoverTargets). */
  targets: PaigeDestination[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  /** Removes it; `sendTo` names the destination the waiting documents go to instead. */
  onConfirm: (sendTo?: string) => void;
}

/**
 * Remove's question, in the workbench's own confirmation dialog (ConfirmActionDialog's look: the
 * themed WorkspaceDialog, Cancel and a danger Remove, focus on Cancel): how many documents are waiting
 * to go there, and, when the job has another destination, whether to send them there instead.
 */
export function RemoveDestinationDialog(props: RemoveDestinationDialogProps) {
  const { row, targets, busy, error, onCancel, onConfirm } = props;
  const [sendTo, setSendTo] = useState('');
  return (
    <WorkspaceDialog
      telemetry="remove-destination"
      title={`Remove ${row.label}?`}
      onClose={onCancel}
      compact
    >
      <div className="confirm-action-content paige-remove-destination">
        <p className={`paige-remove-waiting${row.unsent > 0 ? ' is-waiting' : ''}`}>
          <DestinationIcon kind={row.kind} size={18} />
          <span>{waitingLine(row)}</span>
        </p>
        {targets.length > 0 && (
          <SendToChoice targets={targets} value={sendTo} onChange={setSendTo} />
        )}
        <small className="paige-remove-kept">Nothing already sent there is deleted.</small>
        {error && (
          <p className="paige-destination-error" role="alert">
            {error}
          </p>
        )}
        <DialogActions
          busy={busy}
          onCancel={onCancel}
          onConfirm={() => onConfirm(sendTo || undefined)}
        />
      </div>
    </WorkspaceDialog>
  );
}

/** Cancel and a danger Remove; focus starts on Cancel, the safe choice, as ConfirmActionDialog's does. */
function DialogActions(props: { busy: boolean; onCancel: () => void; onConfirm: () => void }) {
  const { busy, onCancel, onConfirm } = props;
  const actions = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const frame = window.requestAnimationFrame(() =>
      actions.current?.querySelector<HTMLButtonElement>('.secondary-button')?.focus(),
    );
    return () => window.cancelAnimationFrame(frame);
  }, []);
  return (
    <div ref={actions} className="confirm-action-actions">
      <button type="button" className="secondary-button" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      <button type="button" className="danger-button" disabled={busy} onClick={onConfirm}>
        {busy ? 'Removing...' : 'Remove'}
      </button>
    </div>
  );
}

/** Leave them unsent, or send them to one of the job's other destinations instead. */
function SendToChoice({
  targets,
  value,
  onChange,
}: {
  targets: PaigeDestination[];
  value: string;
  onChange: (id: string) => void;
}) {
  const options = [{ id: '', label: "Don't send them", kind: null }, ...targets];
  return (
    <fieldset className="paige-remove-send-to">
      <legend>Send them there instead?</legend>
      {options.map((option) => (
        <label key={option.id} className={value === option.id ? 'is-chosen' : undefined}>
          <input
            type="radio"
            name="paige-remove-send-to"
            checked={value === option.id}
            onChange={() => onChange(option.id)}
          />
          {option.kind && <DestinationIcon kind={option.kind} size={15} />}
          <span>{option.id ? `Send them to ${option.label}` : option.label}</span>
        </label>
      ))}
    </fieldset>
  );
}
